import { useEffect } from "react";
import { useReviews } from "../lib/useReviews.js";
import { useTrips } from "../lib/useTrips.js";
import { useI18n } from "../lib/i18n.jsx";

/**
 * Injects Schema.org JSON-LD describing:
 *  1. The organisation + live aggregateRating from Google Places API
 *  2. One Event per upcoming camp turn (startDate/endDate/location/offers)
 *  3. FAQPage with all FAQ questions (gives expandable Q&A in SERPs)
 *
 * Enables rich-snippet stars in Google SERPs + the Events carousel + FAQ.
 * Updates whenever live data changes; removed on unmount.
 */
const ORG_SCRIPT_ID = "scootive-camp-jsonld-org";
const EVENTS_SCRIPT_ID = "scootive-camp-jsonld-events";
const FAQ_SCRIPT_ID = "scootive-camp-jsonld-faq";

const SITE_URL = "https://scootivecamp.pl";
const VENUE = {
  "@type": "Place",
  name: "Skatepark AvePark",
  address: {
    "@type": "PostalAddress",
    streetAddress: "ul. Krakowiaków 80/98",
    addressLocality: "Warszawa",
    postalCode: "02-255",
    addressCountry: "PL"
  },
  geo: { "@type": "GeoCoordinates", latitude: 52.1638, longitude: 20.9612 }
};

function upsertScript(id, payload) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
}

function buildEvent(trip) {
  const url = trip.booking_url || `${SITE_URL}/#pricing`;
  const startDate = `${trip.date_start}T10:00:00+02:00`;
  const endDate = `${trip.date_finish}T13:00:00+02:00`;
  const seasonName = trip.season === "summer" ? "Letni" : "Zimowy";
  const seasonEn = trip.season === "summer" ? "Summer" : "Winter";
  const name = `${seasonName} obóz hulajnogowy Scootive Camp · ${seasonEn} ${trip.date_start.slice(0, 4)}`;

  let availability = "https://schema.org/InStock";
  if (trip.is_sold_out) availability = "https://schema.org/SoldOut";
  else if (trip.is_few_left) availability = "https://schema.org/LimitedAvailability";

  const price = trip.promo_price ?? trip.price;
  const event = {
    "@context": "https://schema.org",
    "@type": "Event",
    name,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    startDate,
    endDate,
    url,
    location: VENUE,
    image: [`${SITE_URL}/img/${trip.season === "winter" ? "winter" : "summer"}.webp`],
    organizer: { "@type": "Organization", name: "Scootive Camp", url: SITE_URL },
    audience: {
      "@type": "PeopleAudience",
      suggestedMinAge: 8,
      suggestedMaxAge: 16
    }
  };
  if (price != null) {
    event.offers = {
      "@type": "Offer",
      url,
      price,
      priceCurrency: "PLN",
      availability,
      validFrom: new Date().toISOString().slice(0, 10)
    };
  }
  return event;
}

export default function SeoSchema() {
  const { live, rating, ratingCount } = useReviews();
  const { trips } = useTrips();
  const { t } = useI18n();

  useEffect(() => {
    const org = {
      "@context": "https://schema.org",
      "@type": "SportsActivityLocation",
      name: "ScootiveCamp",
      alternateName: "Scootive Camp",
      url: SITE_URL,
      description:
        "7-dniowe obozy hulajnogowe w największym krytym skateparku w Polsce — AvePark Warszawa. Dla dzieci i młodzieży 8–16 lat.",
      image: `${SITE_URL}/img/summer.webp`,
      address: VENUE.address,
      geo: VENUE.geo,
      telephone: "+48 600 000 000",
      sport: "Scootering",
      audience: {
        "@type": "PeopleAudience",
        suggestedMinAge: 8,
        suggestedMaxAge: 16
      },
      sameAs: [
        "https://www.instagram.com/scootive",
        "https://www.facebook.com/scootive",
        "https://www.scootive.pl",
        "https://avepark.pl"
      ]
    };
    if (live && rating != null && ratingCount != null) {
      org.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: Number(rating).toFixed(1),
        reviewCount: ratingCount,
        bestRating: 5,
        worstRating: 1
      };
    }
    upsertScript(ORG_SCRIPT_ID, org);
  }, [live, rating, ratingCount]);

  useEffect(() => {
    const upcoming = (trips || []).filter(
      (t) => !t.is_past && t.date_start && t.date_finish
    );
    if (!upcoming.length) {
      const node = document.getElementById(EVENTS_SCRIPT_ID);
      if (node) node.remove();
      return;
    }
    upsertScript(EVENTS_SCRIPT_ID, upcoming.map(buildEvent));
  }, [trips]);

  useEffect(() => {
    const items = (t?.faq?.items || []).filter((it) => it && it.q && it.a);
    if (!items.length) {
      const node = document.getElementById(FAQ_SCRIPT_ID);
      if (node) node.remove();
      return;
    }
    upsertScript(FAQ_SCRIPT_ID, {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a }
      }))
    });
  }, [t]);

  useEffect(() => {
    return () => {
      [ORG_SCRIPT_ID, EVENTS_SCRIPT_ID, FAQ_SCRIPT_ID].forEach((id) => {
        const node = document.getElementById(id);
        if (node) node.remove();
      });
    };
  }, []);

  return null;
}
